import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Sparkles, Trash2 } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { Confirm } from "./AiImages";

const MAX = 1.9 * 1024 * 1024;

const loadImg = (file) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => res(img);
  img.onerror = () => rej(new Error("This file couldn't be read as an image."));
  img.src = URL.createObjectURL(file);
});

const toBlob = (canvas, type, q) => new Promise((res) => canvas.toBlob(res, type, q));

async function shrink(file, img, keepPng) {
  if (file.size <= MAX && ["image/png", "image/jpeg", "image/webp"].includes(file.type)) return file;
  const scale = Math.min(1, 2400 / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement("canvas");
  c.width = Math.round(img.naturalWidth * scale); c.height = Math.round(img.naturalHeight * scale);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  if (keepPng) { const b = await toBlob(c, "image/png"); if (b && b.size <= MAX) return new File([b], "logo.png", { type: "image/png" }); }
  for (const q of [0.88, 0.8, 0.7, 0.6, 0.5]) {
    const b = await toBlob(c, keepPng ? "image/webp" : "image/jpeg", q);
    if (b && b.size <= MAX) return new File([b], keepPng ? "logo.webp" : "photo.jpg", { type: b.type });
  }
  throw new Error("This photo is too detailed to shrink below 2 MB. Please try another one.");
}

export function ImageField({ api, pid, value, onChange, kind = "image", ratio, hint, testid, ai }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState("");
  const [ask, setAsk] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Please choose a photo under 15 MB."); return; }
    setBusy(true); setWarn("");
    try {
      const img = await loadImg(file);
      if (ratio) {
        const got = img.naturalWidth / img.naturalHeight, want = ratio[0] / ratio[1];
        if (Math.abs(got - want) / want > 0.2) setWarn(`This image is ${img.naturalWidth}×${img.naturalHeight}. It works best at ${ratio[0]}:${ratio[1]}, so parts of it may be cropped.`);
      }
      const f = await shrink(file, img, kind === "logo");
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/projects/${pid}/assets?kind=${kind}`, fd);
      onChange(r.data.id);
      if (f !== file) toast.message(`Image resized to ${(f.size / 1024 / 1024).toFixed(1)} MB for the web.`);
    } catch (err) { toast.error(err.response ? errMsg(err) : err.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center gap-3">
        {value ? <img src={`${API}/assets/${value}`} alt="" className="h-14 w-20 object-cover rounded border" /> : <div className="h-14 w-20 rounded border border-dashed flex items-center justify-center text-[10px] text-slate-400">No image</div>}
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="rounded-full border border-[#0b3d2e]/30 py-1.5 px-3 text-xs inline-flex items-center gap-1 hover:bg-[#f4efe2]" data-testid={`${testid}-upload`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}{value ? "Replace" : "Upload"}</button>
        {value && <button type="button" onClick={() => onChange(null)} className="text-xs text-red-700 inline-flex items-center gap-1" data-testid={`${testid}-remove`}><Trash2 className="w-3.5 h-3.5" />Remove</button>}
        {ai && <button type="button" disabled={ai.running || ai.left < 1} onClick={() => setAsk(true)} className="text-xs inline-flex items-center gap-1 underline disabled:opacity-40" data-testid={`${testid}-ai`}><Sparkles className="w-3.5 h-3.5" />{ai.running ? "Creating…" : `New AI image (${ai.left} left)`}</button>}
      </div>
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
      {warn && <p className="text-[11px] text-amber-700" data-testid={`${testid}-ratio-warning`}>{warn}</p>}
      {ai && <Confirm open={ask} setOpen={setAsk} title="Create a new AI image?" onOk={ai.onRegen} text={`This replaces this image with a new one made for your business and uses 1 of your ${ai.left} remaining AI regenerations.${ai.credit || ""} Please apply your other edits first.`} />}
    </div>);
}
